/**
 * PageSpeed Insights Service
 * 
 * Analyzes website performance using Google PageSpeed Insights API.
 * https://developers.google.com/speed/docs/insights/rest/v5/pagespeedapi/runpagespeed
 */

const PAGESPEED_API_KEY = import.meta.env.VITE_PAGESPEED_API_KEY || '';
const PAGESPEED_API_URL = 'https://pagespeedonline.googleapis.com/pagespeedonline/v5/runPagespeed';

export interface PageSpeedMetrics {
  // Core Web Vitals
  firstContentfulPaint?: number;  // FCP in ms
  largestContentfulPaint?: number; // LCP in ms
  totalBlockingTime?: number;      // TBT in ms
  cumulativeLayoutShift?: number;  // CLS score
  speedIndex?: number;             // Speed Index in ms
  timeToInteractive?: number;      // TTI in ms
  
  // Scores (0-100)
  performanceScore: number;
  accessibilityScore: number;
  bestPracticesScore: number;
  seoScore: number;
  
  // Categories
  overallCategory: 'FAST' | 'AVERAGE' | 'SLOW' | 'UNKNOWN';
  
  // Audit highlights
  opportunities: PageSpeedOpportunity[];
  diagnostics: PageSpeedDiagnostic[];
  
  // Meta
  fetchTime: string;
  finalUrl: string;
  strategy: 'mobile' | 'desktop';
}

export interface PageSpeedOpportunity {
  id: string;
  title: string;
  description: string;
  savings?: string; // e.g., "0.5 s"
  score: number;
}

export interface PageSpeedDiagnostic {
  id: string;
  title: string;
  description: string;
  displayValue?: string;
  score: number | null;
}

export interface PageSpeedResult {
  mobile: PageSpeedMetrics | null;
  desktop: PageSpeedMetrics | null;
  error?: string;
}

/**
 * Parse score from 0-1 to 0-100
 */
function parseScore(score: number | null | undefined): number {
  if (score === null || score === undefined) return 0;
  return Math.round(score * 100);
}

/**
 * Determine overall category based on performance score
 */
function getOverallCategory(score: number): 'FAST' | 'AVERAGE' | 'SLOW' | 'UNKNOWN' {
  if (score >= 90) return 'FAST';
  if (score >= 50) return 'AVERAGE';
  if (score > 0) return 'SLOW';
  return 'UNKNOWN';
}

/**
 * Parse numeric value from display string
 */
function parseNumericValue(value: any): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const match = value.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : undefined;
  }
  return undefined;
}

export const pageSpeedService = {
  /**
   * Check if PageSpeed API is configured
   */
  isConfigured(): boolean {
    return !!PAGESPEED_API_KEY;
  },

  /**
   * Run PageSpeed analysis for a URL
   */
  async analyze(
    url: string, 
    strategy: 'mobile' | 'desktop' = 'mobile'
  ): Promise<PageSpeedMetrics | null> {
    if (!PAGESPEED_API_KEY) {
      console.warn('PageSpeed API key not configured');
      return null;
    }

    try {
      console.log(`🚀 Running PageSpeed analysis (${strategy}):`, url);
      
      const params = new URLSearchParams({
        url,
        key: PAGESPEED_API_KEY,
        strategy: strategy.toUpperCase(),
        category: 'PERFORMANCE',
        // Include all categories
      });
      
      // Add all categories
      ['PERFORMANCE', 'ACCESSIBILITY', 'BEST_PRACTICES', 'SEO'].forEach(cat => {
        params.append('category', cat);
      });
      
      const response = await fetch(`${PAGESPEED_API_URL}?${params.toString()}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('PageSpeed API error:', errorData);
        throw new Error(`PageSpeed API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Extract Lighthouse results
      const lighthouse = data.lighthouseResult;
      if (!lighthouse) {
        console.warn('No Lighthouse results in PageSpeed response');
        return null;
      }
      
      // Extract categories
      const categories = lighthouse.categories || {};
      const audits = lighthouse.audits || {};
      
      // Extract Core Web Vitals
      const metrics: PageSpeedMetrics = {
        performanceScore: parseScore(categories.performance?.score),
        accessibilityScore: parseScore(categories.accessibility?.score),
        bestPracticesScore: parseScore(categories['best-practices']?.score),
        seoScore: parseScore(categories.seo?.score),
        
        // Core Web Vitals from audits
        firstContentfulPaint: parseNumericValue(audits['first-contentful-paint']?.numericValue),
        largestContentfulPaint: parseNumericValue(audits['largest-contentful-paint']?.numericValue),
        totalBlockingTime: parseNumericValue(audits['total-blocking-time']?.numericValue),
        cumulativeLayoutShift: parseNumericValue(audits['cumulative-layout-shift']?.numericValue),
        speedIndex: parseNumericValue(audits['speed-index']?.numericValue),
        timeToInteractive: parseNumericValue(audits['interactive']?.numericValue),
        
        overallCategory: getOverallCategory(parseScore(categories.performance?.score)),
        
        opportunities: [],
        diagnostics: [],
        
        fetchTime: lighthouse.fetchTime || new Date().toISOString(),
        finalUrl: lighthouse.finalUrl || url,
        strategy
      };
      
      // Extract opportunities (audits with savings)
      const opportunityAudits = [
        'render-blocking-resources',
        'unused-css-rules',
        'unused-javascript',
        'modern-image-formats',
        'offscreen-images',
        'unminified-css',
        'unminified-javascript',
        'efficient-animated-content',
        'duplicated-javascript',
        'legacy-javascript',
        'uses-responsive-images',
        'uses-optimized-images',
        'uses-text-compression',
        'server-response-time',
        'redirects',
        'uses-rel-preconnect',
        'uses-rel-preload'
      ];
      
      opportunityAudits.forEach(auditId => {
        const audit = audits[auditId];
        if (audit && audit.score !== null && audit.score < 1) {
          metrics.opportunities.push({
            id: auditId,
            title: audit.title || auditId,
            description: audit.description || '',
            savings: audit.displayValue,
            score: parseScore(audit.score)
          });
        }
      });
      
      // Sort opportunities by impact (lowest score first)
      metrics.opportunities.sort((a, b) => a.score - b.score);
      metrics.opportunities = metrics.opportunities.slice(0, 5); // Top 5
      
      // Extract diagnostics
      const diagnosticAudits = [
        'dom-size',
        'critical-request-chains',
        'network-requests',
        'network-rtt',
        'network-server-latency',
        'main-thread-tasks',
        'bootup-time',
        'mainthread-work-breakdown',
        'font-display',
        'third-party-summary'
      ];
      
      diagnosticAudits.forEach(auditId => {
        const audit = audits[auditId];
        if (audit && audit.displayValue) {
          metrics.diagnostics.push({
            id: auditId,
            title: audit.title || auditId,
            description: audit.description || '',
            displayValue: audit.displayValue,
            score: audit.score !== undefined ? parseScore(audit.score) : null
          });
        }
      });
      
      metrics.diagnostics = metrics.diagnostics.slice(0, 5); // Top 5
      
      console.log(`✅ PageSpeed analysis complete (${strategy}):`, {
        performance: metrics.performanceScore,
        seo: metrics.seoScore,
        accessibility: metrics.accessibilityScore
      });
      
      return metrics;
      
    } catch (error) {
      console.error('PageSpeed analysis failed:', error);
      return null;
    }
  },

  /**
   * Run full PageSpeed analysis (mobile + desktop)
   */
  async analyzeFullAsync(url: string): Promise<PageSpeedResult> {
    console.log('📊 Running full PageSpeed analysis for:', url);
    
    try {
      // Run mobile and desktop in parallel
      const [mobile, desktop] = await Promise.all([
        this.analyze(url, 'mobile'),
        this.analyze(url, 'desktop')
      ]);
      
      return { mobile, desktop };
      
    } catch (error) {
      console.error('Full PageSpeed analysis failed:', error);
      return {
        mobile: null,
        desktop: null,
        error: (error as Error).message
      };
    }
  },

  /**
   * Get a summary text for the performance
   */
  getPerformanceSummary(metrics: PageSpeedMetrics): string {
    const score = metrics.performanceScore;
    
    if (score >= 90) {
      return 'Exzellente Performance - Die Website lädt sehr schnell.';
    } else if (score >= 70) {
      return 'Gute Performance - Kleine Optimierungen möglich.';
    } else if (score >= 50) {
      return 'Durchschnittliche Performance - Optimierungspotenzial vorhanden.';
    } else {
      return 'Schlechte Performance - Dringend Optimierung empfohlen.';
    }
  },

  /**
   * Get color class for score
   */
  getScoreColor(score: number): 'green' | 'orange' | 'red' {
    if (score >= 90) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  }
};
